/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/16/22
 */

import React from "react";
import {Link, Stack} from "@mui/material";
import {
	DataGrid, getGridStringOperators,
	GridColumnVisibilityModel,
	GridFilterModel,
	GridSortModel,
	GridToolbar,
	GridValueFormatterParams, GridValueGetterParams
} from "@mui/x-data-grid";
import {useQuery} from "urql";
import {
	CalendarEventsQueryVars,
	CalendarEventsResponse,
	SearchCalendarEventsQuery
} from "../../graphql/queries/calendar-events";
import {VegaEvent} from "../../interfaces/Global";
import {eventTypes, getEventType} from "../../calendar/event-types";
import Avatar from "../../components/Avatar";
import {
	EmployeePictureUrlQuery,
	EmployeePictureUrlResponse,
	EmployeePictureUrlVars
} from "../../graphql/queries/employees";
import FilterToolbar from "./FilterToolbar";
import {envType} from "../../context/environment";
import {dateTimeCol} from "../../dates-helper";

const getGraphQLVarsForFilterModel = (filterModel: GridFilterModel) => {
	let vars: Record<string, any> = {};
	const suffixes: Record<string, string> = {
		"is": "", "not": "Not", "after": "Gt", "onOrAfter": "Gte", "before": "Lt", "onOrBefore": "Lte",
		"isEmpty": "", "isNotEmpty": "", "contains": "Like", "equals": "", "startsWith": "Like", "endsWith": "Like",
		"=": "", ">": "Gt", ">=": "Gte", "<": "Lt", "<=": "Lte", "!=": "Not"
	}
	filterModel.items.forEach(item => {
		const attributeName = item.columnField + ((item.operatorValue && suffixes[item.operatorValue]) || "");
		let sendValue = item.value;
		if (typeof sendValue === "string") {
			if (item.operatorValue === "contains") sendValue = `%${sendValue}%`;
			if (item.operatorValue === "startsWith") sendValue = `${sendValue}%`;
			if (item.operatorValue === "endsWith") sendValue = `%${sendValue}`;
		}
		vars[attributeName] = sendValue;
	});
	return vars;
}

const storageColumnVisibilityModel = 'vega.browse.calendar-events.column-visibility';

export default function EventsReport() {

	const [limit, setLimit] = React.useState(20);
	const [offset, setOffset] = React.useState(0);
	const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 'creationDate', sort: 'desc' }]);
	const [filterModel, setFilterModel] = React.useState<GridFilterModel>({items: []});
	const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(() => {
		const storedModel = localStorage.getItem(storageColumnVisibilityModel);
		return storedModel ? JSON.parse(storedModel) : {};
	});
	const [filterVars, setFilterVars] = React.useState<Record<string, any>>({});

	const [calendarEventsResult] = useQuery<CalendarEventsResponse, CalendarEventsQueryVars>({
		query: SearchCalendarEventsQuery,
		requestPolicy: "cache-and-network",
		variables: {
			...getGraphQLVarsForFilterModel(filterModel),
			...filterVars,
			limit,
			offset,
			orderBy: (sortModel?.[0]?.sort === "desc" ? "-":"") + (sortModel?.[0]?.field ?? "creationDate") },
	});

	const calendarEvents = React.useMemo(() => calendarEventsResult.data?.calendarEvents.items || [], [calendarEventsResult]);
	const employeeIds = React.useMemo(() => ([
		...((columnVisibilityModel.createdBy ?? true) ? (calendarEventsResult.data?.calendarEvents.items || []).map(e => e.createdBy).filter(i => i) : []),
		...((columnVisibilityModel.employeeId ?? true) ? (calendarEventsResult.data?.calendarEvents.items || []).map(e => e.employeeId).filter(i => i) : []),
		...((columnVisibilityModel.managerId ?? true) ? (calendarEventsResult.data?.calendarEvents.items || []).map(e => e.employee?.manager?.id).filter(i => i) : []),
	].filter((v, i, a) => a.indexOf(v) === i)), [calendarEventsResult.data?.calendarEvents.items, columnVisibilityModel]);

	const [pictureResult] = useQuery<EmployeePictureUrlResponse, EmployeePictureUrlVars>({
		query: EmployeePictureUrlQuery,
		variables: {
			employees: employeeIds as string[]
		},
		pause: employeeIds.length === 0 || envType !== "prod"
	});

	const pictureUrls = React.useMemo(() => pictureResult.data?.employeePictureUrl || {}, [pictureResult]);

	return (
		<Stack>
			<FilterToolbar
				filters={filterVars}
				setFilters={setFilterVars}
			/>
			<DataGrid
				rows={calendarEvents}
				autoHeight
				hideFooterSelectedRowCount
				loading={calendarEventsResult.fetching}
				density={"compact"}
				sortingMode="server"
				filterMode="server"
				sortModel={sortModel}
				onSortModelChange={setSortModel}
				onFilterModelChange={setFilterModel}
				disableColumnFilter={true}
				pagination
				pageSize={limit}
				rowsPerPageOptions={[10, 20, 50, 100]}
				rowCount={calendarEventsResult.data?.calendarEvents.total || 0}
				paginationMode="server"
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={(newModel) => {
					localStorage.setItem(storageColumnVisibilityModel, JSON.stringify(newModel));
					setColumnVisibilityModel(newModel)
				}}
				onPageSizeChange={(p) => setLimit(p)}
				onPageChange={(page) => setOffset(page*limit)}
				components={{ Toolbar: GridToolbar }}
				columns={[
					{ field: "id", headerName: "ID" },
					{ field: "creationDate", ...dateTimeCol, headerName: "Creation Date" },
					{ field: "createdBy",
						headerName: "Created By",
						headerAlign: "center",
						flex: 1,
						filterOperators: getGridStringOperators().filter(op => op.value === "equals"),
						valueGetter: (params) => {
							return params.row?.createdByEmployee?.displayName || params.value;
						},
						renderCell: (params) => {
							const model = params.row as VegaEvent;
							const displayName = model.createdByEmployee?.displayName;
							if (displayName) {
								const href = "https://directory.prod.atl-paas.net/employees/" + model.createdBy;
								return <Stack direction={"row"} spacing={.5} alignItems={"center"}>
									<Avatar
										src={envType === "prod" ? pictureUrls[model.createdBy] : undefined}
										id={envType === "prod" ? undefined : model.createdBy}
										size={"xxs"}
									/>
									<Link color={"primary"} underline={"hover"} target={"_blank"} href={href}>{displayName}</Link>
								</Stack>;
							}
							return params.value;
						}
					},
					{
						field: "typeId",
						type: "singleSelect",
						valueOptions: eventTypes.filter(t => t.parentId).map(t => t.id),
						valueFormatter: (params: GridValueFormatterParams) => {
							return (params.value && getEventType(params.value as string)?.name) || params.value;
						},
						headerName: "Type",
						headerAlign: "center",
						align: "center",
						flex: 1
					},
					{
						field: "typeParentId",
						type: "singleSelect",
						valueOptions: eventTypes.filter(t => !t.parentId).map(t => t.id),
						valueGetter: (params: GridValueGetterParams) => {
							return params.row?.type?.parent?.id;
						},
						valueFormatter: (params: GridValueFormatterParams) => {
							return eventTypes.find(t => t.id === params.value)?.name;
						},
						headerName: "Category",
						headerAlign: "center",
						align: "center",
						flex: 1
					},
					{
						field: "capacityModifier",
						headerName: "Capacity",
						type: "number",
						headerAlign: "center",
						align: "center",
						valueFormatter: (params: GridValueFormatterParams) => {
							return Math.round(Number(params.value)*100) + "%";
						},
					},
					{ field: "startDatetime", ...dateTimeCol, headerName: "Start Date" },
					{ field: "endDatetime", ...dateTimeCol, headerName: "End Date" },
					{
						field: "employeeId",
						headerName: "Employee ID",
						headerAlign: "center",
						flex: 1,
						filterOperators: getGridStringOperators().filter(op => op.value === "equals"),
						valueGetter: (params) => {
							return params.row?.employee?.displayName || params.value;
						},
						renderCell: (params) => {
							const model = params.row as VegaEvent;
							const displayName = model.employee?.displayName;
							if (displayName) {
								const href = "https://directory.prod.atl-paas.net/employees/" + model.employeeId;
								return <Stack direction={"row"} spacing={.5} alignItems={"center"}>
									<Avatar
										src={envType === "prod" ? pictureUrls[model.employeeId] : undefined}
										id={envType === "prod" ? undefined : model.employeeId}
										size={"xxs"}
									/>
									<Link color={"primary"} underline={"hover"} target={"_blank"} href={href}>{displayName}</Link>
								</Stack>;
							}
							return params.value;
						}
					},
					{
						field: "managerId",
						headerName: "Manager ID",
						headerAlign: "center",
						flex: 1,
						filterOperators: getGridStringOperators().filter(op => op.value === "equals"),
						valueGetter: (params) => {
							return params.row?.employee?.manager?.displayName || params.value;
						},
						renderCell: (params) => {
							const model = params.row as VegaEvent;
							const displayName = model.employee?.manager?.displayName;
							if (displayName) {
								const id = model.employee?.manager?.id ?? "";
								const href = "https://directory.prod.atl-paas.net/employees/" + id;
								return <Stack direction={"row"} spacing={.5} alignItems={"center"}>
									<Avatar
										src={envType === "prod" ? pictureUrls[id] : undefined}
										id={envType === "prod" ? undefined : id}
										size={"xxs"}
									/>
									<Link color={"primary"} underline={"hover"} target={"_blank"} href={href}>{displayName}</Link>
								</Stack>;
							}
							return params.value;
						}
					},
					{ field: "isConfirmed", type: "boolean", headerName: "Confirmed" }
				]}
			/>
		</Stack>
	);

}